import {InteractionAction, InteractionEvent, InteractionTrigger} from '@frontapp/editor-html';
import {isUndefined} from 'lodash';

import {
  EventEntryPointsEnum,
  InterfaceEventsEnum,
  trackInterfaceEvent,
} from '../../../../../libs/shared/core/src/services/tracking/tracking';

/*
 * Constants.
 */

/** Values for the `format_option` property on `comment_composer.formatting.add` events. */
export enum CommentFormattingOptionsEnum {
  BOLD = 'bold',
  ITALIC = 'italic',
  UNDERLINE = 'underline',
  STRIKETHROUGH = 'strikethrough',
  BLOCKQUOTE = 'blockquote',
  UNORDERED_LIST = 'unordered_list',
  ORDERED_LIST = 'ordered_list',
  LINK = 'link',
  INLINE_CODE = 'inline_code',
  CODE_BLOCK = 'code_block',
}

/** Maps editor triggers to their tracking values. */
const entryPointsByEditorTrigger: EditorTriggerToEntryPointMap = {
  [InteractionTrigger.KEYBOARD_SHORTCUT]: EventEntryPointsEnum.KEYBOARD_SHORTCUT,
  [InteractionTrigger.KEYBOARD_TYPING]: EventEntryPointsEnum.MARKDOWN,
};

/** Maps editor actions to their tracking values. */
const formatOptionsByEditorAction: EditorActionToFormattingOptionMap = {
  [InteractionAction.TOGGLE_BOLD]: CommentFormattingOptionsEnum.BOLD,
  [InteractionAction.TOGGLE_ITALIC]: CommentFormattingOptionsEnum.ITALIC,
  [InteractionAction.TOGGLE_UNDERLINE]: CommentFormattingOptionsEnum.UNDERLINE,
  [InteractionAction.TOGGLE_STRIKETHROUGH]: CommentFormattingOptionsEnum.STRIKETHROUGH,
  [InteractionAction.SET_BLOCKQUOTE]: CommentFormattingOptionsEnum.BLOCKQUOTE,
  [InteractionAction.SET_UNORDERED_LIST]: CommentFormattingOptionsEnum.UNORDERED_LIST,
  [InteractionAction.SET_ORDERED_LIST]: CommentFormattingOptionsEnum.ORDERED_LIST,
  [InteractionAction.SET_LINK]: CommentFormattingOptionsEnum.LINK,
  [InteractionAction.SET_INLINE_CODE]: CommentFormattingOptionsEnum.INLINE_CODE,
  [InteractionAction.SET_CODE_BLOCK]: CommentFormattingOptionsEnum.CODE_BLOCK,
};

/*
 * Types.
 */

type EditorActionToFormattingOptionMap = {
  [action in InteractionAction]?: CommentFormattingOptionsEnum;
};

type EditorTriggerToEntryPointMap = {
  [trigger in InteractionTrigger]?: EventEntryPointsEnum;
};

/*
 * API.
 */

/** Tracks `comment_composer.formatting.add` events. */
export function trackFormattingEvent(
  conversationId: number | undefined,
  formatOption: CommentFormattingOptionsEnum,
  entryPoint: EventEntryPointsEnum,
) {
  trackInterfaceEvent(InterfaceEventsEnum.COMMENT_COMPOSER_FORMATTING_ADD, {
    conversation_id: conversationId,
    format_option: formatOption,
    entry_point: entryPoint,
  });
}

export function trackManualFormattingEvent(
  conversationId: number | undefined,
  formatOption: CommentFormattingOptionsEnum,
) {
  trackFormattingEvent(conversationId, formatOption, EventEntryPointsEnum.MANUAL);
}

/** Tracks editor interaction events as `comment_composer.formatting.add` events. */
export function trackFormattingEventForEditorInteraction(
  conversationId: number | undefined,
  event: InteractionEvent,
) {
  const formatOption = formatOptionsByEditorAction[event.action];
  const entryPoint = entryPointsByEditorTrigger[event.trigger];

  // If this editor action is not a known formatting action, do nothing.
  if (isUndefined(formatOption) || isUndefined(entryPoint)) {
    return;
  }

  trackFormattingEvent(conversationId, formatOption, entryPoint);
}
